import Phaser from 'phaser';
import { furnitureCatalog, findFurniture, furnitureSortY, type ZoneConfig, type ZoneAssetUrls } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { FLOOR_DEPTHS } from './FloorRenderer.js';
import { spawnFurnitureImage } from './FurnitureTextures.js';

/** Avance d'un objet sur le mur de la même rangée (bien moins qu'un pixel de tri en Y : 0,1). */
const WALL_TIE = 0.001;

/**
 * xhrSettings avec header Authorization — pour les fichiers chargés par le
 * loader Phaser (tilemapTiledJSON, image, spritesheet) depuis une route
 * protégée (/assets/tenants/:tenantId/*). Phaser ne suit pas le mécanisme
 * fetch() classique, mais son XHR interne supporte des headers custom via
 * xhrSettings — pas besoin de mettre le JWT dans l'URL.
 */
function authXhrSettings(jwt: string): Phaser.Types.Loader.XHRSettingsObject {
  const settings = Phaser.Loader.XHRSettings();
  settings.headers = { Authorization: `Bearer ${jwt}` };
  return settings;
}

/**
 * ZoneLoader — Gestion du chargement et du préchargement des assets de zone.
 *
 * STRATÉGIE DE CACHE :
 *   Les URLs /assets/tenants/{id}/... sont stables → le navigateur les met en cache.
 *   Phaser met aussi les assets en cache interne (this.textures, this.cache.json).
 *   On vérifie le cache Phaser avant de relancer un chargement réseau.
 *
 * TILESETS DYNAMIQUES (maps Copyhouse) :
 *   Le TMJ contient les noms de tilesets sous forme de chemins relatifs
 *   (ex: "../copyhouse_maps/tilesets/WA_Exterior.png").
 *   ZoneLoader écoute filecomplete du TMJ, extrait le basename de chaque
 *   tileset.image, et charge /assets/shared/tilesets/{basename}.webp
 *   via la route proxy (manifest hash-based côté API).
 *   Clé texture : "tileset:{zoneId}:{index}" — même convention que ZoneScene.
 *
 * PRÉCHARGEMENT ZONE VOISINE :
 *   Après chargement complet de la zone courante, on lance en background
 *   le préchargement de la zone voisine la plus probable (premier portail).
 *   Quand l'utilisateur traverse le portail → assets déjà en mémoire.
 *
 * RÈGLE : Cette classe ne connaît pas React, Zustand, ou EventBus.
 *   Elle est utilisée uniquement depuis les Scenes Phaser.
 */
export class ZoneLoader {
  private readonly scene: Phaser.Scene;
  private preloadedZones = new Set<string>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Charge tous les assets d'une zone.
   * Doit être appelé dans preload() d'une Scene Phaser.
   * Vérifie le cache avant tout chargement réseau.
   */
  loadZone(config: ZoneConfig, urls: ZoneAssetUrls, jwt: string): void {
    const { load: loader, textures, cache } = this.scene;
    const mapKey = `map:${config.id}`;

    // Helper : une fois le JSON TMJ disponible, enregistre les images tileset
    // dans la file du loader Phaser (pendant preload, cela fonctionne de manière
    // transparente — Phaser traite les nouveaux fichiers avant d'émettre 'complete').
    const loadTilesetsFromData = (tmjData: unknown) => {
      const tilesets = (tmjData as {
        tilesets?: Array<{ name: string; image?: string; source?: string; tilewidth?: number; tileheight?: number }>;
      }).tilesets ?? [];

      tilesets.forEach((ts, i) => {
        const key = `tileset:${config.id}:${i}`;
        if (textures.exists(key)) return;

        // Construit l'URL proxy depuis le chemin image du TMJ.
        //
        // Deux familles de tilesets :
        //   A) LimeZu (chemin contient Theme_Sorter_32x32/ ou Room_Builder_32x32)
        //      → route /assets/tilesets/* → clé S3 directe (PNG)
        //   B) Copyhouse / WA (tout le reste)
        //      → route /assets/shared/tilesets/:basename.webp → manifest hashé
        const imagePath = ts.image ?? ts.source ?? ts.name;

        let tilesetUrl: string;
        if (imagePath.includes('Theme_Sorter_32x32/')) {
          const filename = imagePath.split('Theme_Sorter_32x32/').pop()!;
          tilesetUrl = `${API_BASE}/assets/tilesets/Theme_Sorter_32x32/${filename}`;
        } else if (imagePath.includes('Room_Builder_32x32')) {
          tilesetUrl = `${API_BASE}/assets/tilesets/Room_Builder_32x32.png`;
        } else {
          // Copyhouse / WA : basename sans extension + .webp → route manifest
          const basename = imagePath.split('/').pop()!.replace(/\.[^.]+$/, '');
          tilesetUrl = `${API_BASE}/assets/shared/tilesets/${basename}.webp`;
        }

        console.log(`[ZoneLoader] tileset[${i}] "${imagePath}" → ${tilesetUrl}`);
        const tw = ts.tilewidth ?? 32;
        const th = ts.tileheight ?? 32;
        loader.spritesheet(key, tilesetUrl, { frameWidth: tw, frameHeight: th });
      });
    };

    // ── Fichier TMJ ───────────────────────────────────────────────────────────

    if (cache.tilemap.exists(mapKey)) {
      // Déjà en cache Phaser — extraire le JSON brut et charger les tilesets
      // cache.tilemap.get(key) = { name: string, data: rawTMJ }
      const cached = cache.tilemap.get(mapKey) as { data?: unknown } | undefined;
      loadTilesetsFromData(cached?.data ?? {});
    } else {
      // Lancer le chargement du TMJ, puis écouter filecomplete pour les tilesets
      loader.tilemapTiledJSON(mapKey, urls.tmj, authXhrSettings(jwt));

      // Phaser émet FILE_COMPLETE (key, type, data) pour chaque fichier chargé.
      // On filtre sur mapKey pour réagir uniquement au TMJ de cette zone.
      const onFileDone = (key: string, _type: string, data: unknown) => {
        if (key !== mapKey) return;
        loader.off(Phaser.Loader.Events.FILE_COMPLETE, onFileDone);
        loadTilesetsFromData(data);
      };
      loader.on(Phaser.Loader.Events.FILE_COMPLETE, onFileDone);
    }

    // ── Tilesets WebP explicites (liste fournie par le serveur, si non vide) ──
    // Pour les maps avec tilesets déclarés explicitement dans ZoneAssetUrls.
    // Pour les maps Copyhouse (tilesets embedded), urls.tilesets est vide —
    // les tilesets sont découverts dynamiquement ci-dessus.
    urls.tilesets.forEach((url, index) => {
      const key = `tileset:${config.id}:${index}`;
      if (!textures.exists(key)) {
        loader.image(key, url, authXhrSettings(jwt));
      }
    });

    // ── Spritesheet avatars ───────────────────────────────────────────────────
    // characters.webp : 192×128px, grille 12×8, frames 16×16px.
    // 8 personnages (P0-P7) × 3 cols anim × 4 lignes direction = 96 frames.
    // Proxy sans auth → /assets/shared/tilesets/characters.webp → MinIO.
    if (!textures.exists('avatars')) {
      loader.spritesheet('avatars', `${API_BASE}/assets/shared/tilesets/characters.webp`, {
        frameWidth:  16,
        frameHeight: 16,
      });
    }
  }

  /**
   * Précharge en background les assets de la zone voisine.
   * Appelé après que la zone courante soit fully loaded (dans create()).
   *
   * Utilise un LoaderPlugin temporaire pour ne pas interférer
   * avec le loader principal de la scène active.
   */
  async prefetchAdjacentZone(
    targetConfig: ZoneConfig,
    targetUrls: ZoneAssetUrls,
    jwt: string,
  ): Promise<void> {
    const zoneId = targetConfig.id;
    if (this.preloadedZones.has(zoneId)) return;

    this.preloadedZones.add(zoneId);

    // Phaser ne supporte pas nativement un loader "background" sans scène.
    // On utilise fetch() natif du navigateur pour mettre les assets en cache HTTP.
    // Quand Phaser demandera les mêmes URLs plus tard, le navigateur servira
    // depuis son cache sans requête réseau.
    const urlsToPreload = [
      targetUrls.tmj,
      targetUrls.wam,
      ...targetUrls.tilesets,
    ];

    // Lancement en arrière-plan — pas d'await intentionnel
    Promise.allSettled(
      urlsToPreload.map((url) =>
        fetch(url, { cache: 'force-cache', headers: { Authorization: `Bearer ${jwt}` } }).catch(() => {
          // Silencieux : le préchargement est une optimisation, pas un prérequis
        }),
      ),
    );
  }

  /**
   * Vérifie si une zone est déjà entièrement chargée dans le cache Phaser.
   */
  isZoneCached(zoneId: string): boolean {
    return (
      this.scene.cache.tilemap.exists(`map:${zoneId}`) &&
      this.preloadedZones.has(zoneId)
    );
  }

  // ── Furniture ───────────────────────────────────────────────────────────

  /**
   * Enregistre chaque WebP du catalogue mobilier dans le loader Phaser.
   * Doit etre appele dans preload() d'une Scene.
   */
  preloadFurniture(): void {
    const { load: loader, textures } = this.scene;

    // Palette de l'éditeur uniquement — les entrées masquées (hors sélection,
    // gardées pour le mobilier déjà posé) sont chargées à la demande.
    for (const entry of furnitureCatalog) {
      if (entry.hidden || textures.exists(entry.id)) continue;
      if (entry.anim) loader.spritesheet(entry.id, entry.url, { frameWidth: entry.anim.frameWidth, frameHeight: entry.anim.frameHeight });
      else loader.image(entry.id, entry.url);
    }
  }

  /**
   * Instancie un sprite mobilier aux coordonnees tile donnees.
   * Utilise l'anchor du catalogue et convertit les tiles en pixels (× tileSize).
   */
  spawnFurniture(id: string, tileX: number, tileY: number, layer = 'furniture'): Phaser.GameObjects.Image {
    const entry = findFurniture(id);
    if (!entry) {
      throw new Error(`[ZoneLoader] furniture id inconnu: ${id}`);
    }

    const ts = entry.tileSize;
    const px = tileX * ts;
    // Ancre bas-gauche : Y = bas du tile occupé
    const bottomY = (tileY + 1) * ts;

    const image = spawnFurnitureImage(this.scene, entry, px, bottomY);

    // Depth sorting cohérent avec les avatars (100 + y / 10)
    if (layer === 'furnitureTop') {
      image.setDepth(999);
    } else if ((layer === 'floor' && !entry.standing) || entry.walkable) {
      // Objet au sol (tapis, paillasson) ou pont/ponton (walkable, même posé
      // en « Mobilier » avant le 2026-09-28) : toujours sous les avatars.
      image.setDepth(FLOOR_DEPTHS.floorObjects);
    } else {
      // + WALL_TIE : un objet posé sur un mur (fenêtre, tableau mural, applique)
      // passe devant la tuile de mur de la même rangée, à profondeur égale.
      image.setDepth(100 + furnitureSortY(entry, bottomY) / 10 + WALL_TIE);
    }

    return image;
  }
}
